import { type JSX, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ErrorComponent } from '../Error/Error';
import { NotFoundImage } from '../Error/pictures/NotFound';

export const AppNames = {
  'DSP UI': 'DSP UI',
  OpenXSelect: 'OpenXSelect',
  OXControl: 'OXControl',
  TraceX: 'TraceX',
  Unity: 'Unity',
} as const;

export type PageNotFoundProps = {
  appName?: keyof typeof AppNames;
  withHeader?: boolean;
  homeHref?: string;
};

const pageTitle = 'Page Not Found';

export const PageNotFound = ({ appName, withHeader = true, homeHref }: PageNotFoundProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    if (homeHref) {
      window.location.href = homeHref;
      return;
    }

    navigate('/');
  }, [navigate, homeHref]);

  return (
    <ErrorComponent
      title={pageTitle}
      titlePrefix={appName}
      withHeader={withHeader}
      subtitle={t("We've looked everywhere and we just can't find that page in our universe.")}
      actions={[
        {
          children: t('Take me home please'),
          color: 'secondary',
          'data-test': 'return-home',
          onClick: handleClick,
          variant: 'contained',
        },
      ]}
      PictureComponent={NotFoundImage}
      data-test="page-not-found"
    />
  );
};
